/* eslint-disable no-console */
import React, { useState, useCallback, useMemo, useEffect } from "react";
import toast from "react-hot-toast";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import {
  CotizadorProgrammaticCalculadora,
  CotizadorProgrammaticDuracion,
  CotizadorProgrammaticFrecuencia,
  CotizadorProgrammaticHorasOn,
  CotizadorProgrammaticInventario,
  CotizadorProgrammaticPlazo,
} from "../../components";
import { getEdificios } from "../../utils";
import "./stylesCotizadorProgrammaticEditar.scss";

export const CotizadorProgrammaticEditar = () => {
  const { id } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  const oportunidad = state?.oportunidad;

  const [loading, setLoading] = useState(true);
  const [, setEdificios] = useState([]);
  const [resetCounter, setResetCounter] = useState(0); // Add reset counter

  // Initialize edificiosSeleccionados for each circuit
  const initialEdificios = oportunidad?.edificiosparticipantes
    ? typeof oportunidad.edificiosparticipantes === "string"
      ? oportunidad.edificiosparticipantes.split(",").map((id) => id.trim())
      : oportunidad.edificiosparticipantes
    : [];
  const [inventarioData, setInventarioData] = useState({
    ascensoresSeleccionados: 0,
    audienciaSeleccionadaAscensor: 0,
    audienciaSeleccionadaCowork: 0,
    audienciaSeleccionadaTotal: 0,
    audienciaSeleccionadaTotems: 0,
    coworkSeleccionados: 0,
    edificiosSeleccionadosAscensores: [],
    edificiosSeleccionadosTotems: [],
    edificiosSeleccionadosCowork: [],
    edificiosSeleccionadosEspera: [],
    esperaSeleccionados: 0,
    totemsSeleccionados: 0,
  });
  const [plazo, setPlazo] = useState(oportunidad?.periodopauta || 0);
  const [frecuencia, setFrecuencia] = useState(oportunidad?.formatopauta ? String(oportunidad.formatopauta) : "15");
  const [horasSeleccionadas, setHorasSeleccionadas] = useState({
    horas: oportunidad?.horas_pauta || 0,
    intervalos: oportunidad?.intervalos ? JSON.parse(oportunidad.intervalos) : [],
    options: oportunidad?.options ? JSON.parse(oportunidad.options) : [],
  });
  const [duracion, setDuracion] = useState(oportunidad?.duracion || 10);
  const [isProgrammaticSelected, setIsProgrammaticSelected] = useState(oportunidad?.tipo === "p");
  const [isDigitalSelected, setIsDigitalSelected] = useState(oportunidad?.tipo === "d");
  const [resetInventario, setResetInventario] = useState(false);
  const [includeInstallingBuildingsUserSelection, setIncludeInstallingBuildingsUserSelection] = useState(
    oportunidad?.instalacion ?? false
  );

  // Agregar las nuevas constantes aquí
  const defaultEdificiosAscensores = oportunidad?.pantallas_ascensores
    ? oportunidad.pantallas_ascensores.split(",").filter(Boolean).map(Number)
    : [];
  const defaultEdificiosTotems = oportunidad?.pantallas_circulacion
    ? oportunidad.pantallas_circulacion.split(",").filter(Boolean).map(Number)
    : [];
  const defaultEdificiosEspera = oportunidad?.pantallas_espera
    ? oportunidad.pantallas_espera.split(",").filter(Boolean).map(Number)
    : [];
  const defaultEdificiosCowork = oportunidad?.pantallas_cowork
    ? oportunidad.pantallas_cowork.split(",").filter(Boolean).map(Number)
    : [];

  useEffect(() => {
    const fetchEdificios = async () => {
      try {
        const edificiosData = await getEdificios({ pais: oportunidad?.pais || "Chile" });
        setEdificios(edificiosData);

        // Verificar si la oportunidad incluye edificios en INSTALACIÓN
        const edificiosIds = [
          ...(oportunidad?.pantallas_ascensores?.split(",").filter(Boolean).map(Number) || []),
          ...(oportunidad?.pantallas_circulacion?.split(",").filter(Boolean).map(Number) || []),
          ...(oportunidad?.pantallas_espera?.split(",").filter(Boolean).map(Number) || []),
          ...(oportunidad?.pantallas_cowork?.split(",").filter(Boolean).map(Number) || []),
        ];
        const hasInstallingBuildings = edificiosData.some(
          (edificio) =>
            edificiosIds.includes(edificio.id) &&
            (edificio.estado === "INSTALACIÓN" || edificio.estado === "REACTIVACIÓN")
        );
        setIncludeInstallingBuildingsUserSelection(hasInstallingBuildings);

        setLoading(false);
      } catch (error) {
        console.error("Error al cargar edificios:", error);
        toast.error("Error al cargar los edificios.");
        navigate("/admin/ventas-detalle");
      }
    };

    if (!oportunidad || String(oportunidad.id) !== id) {
      toast.error("No se encontró la cotización para editar o el ID no coincide");
      navigate("/admin/ventas-detalle");
    } else {
      fetchEdificios();
    }
  }, [oportunidad, id, navigate]);

  const handleProgrammaticChange = useCallback((isProgrammatic) => {
    setIsProgrammaticSelected(isProgrammatic);
    setIsDigitalSelected(!isProgrammatic);
    setResetInventario(true);
    setInventarioData({
      ascensoresSeleccionados: 0,
      audienciaSeleccionadaAscensor: 0,
      audienciaSeleccionadaCowork: 0,
      audienciaSeleccionadaTotal: 0,
      audienciaSeleccionadaTotems: 0,
      coworkSeleccionados: 0,
      edificiosSeleccionadosAscensores: [],
      edificiosSeleccionadosCowork: [],
      edificiosSeleccionadosTotems: [],
      edificiosSeleccionadosEspera: [],
      esperaSeleccionados: 0,
      totemsSeleccionados: 0,
    });
    setHorasSeleccionadas({ horas: 0, intervalos: [], options: [] });
    setPlazo(0);
    setFrecuencia("");
    setDuracion(0);
    setResetCounter((prev) => prev + 1);
  }, []);

  const handleDigitalChange = useCallback((isDigital) => {
    setIsDigitalSelected(isDigital);
    setIsProgrammaticSelected(!isDigital);
    setResetInventario(true);
    setInventarioData({
      ascensoresSeleccionados: 0,
      audienciaSeleccionadaAscensor: 0,
      audienciaSeleccionadaCowork: 0,
      audienciaSeleccionadaTotal: 0,
      audienciaSeleccionadaTotems: 0,
      coworkSeleccionados: 0,
      edificiosSeleccionadosAscensores: [],
      edificiosSeleccionadosCowork: [],
      edificiosSeleccionadosTotems: [],
      edificiosSeleccionadosEspera: [],
      esperaSeleccionados: 0,
      totemsSeleccionados: 0,
    });
    setHorasSeleccionadas({ horas: 0, intervalos: [], options: [] });
    setPlazo(0);
    setFrecuencia("");
    setDuracion(0);
    setResetCounter((prev) => prev + 1);
  }, []);

  const handleSubmitInventario = useCallback((data) => {
    setInventarioData(data);
  }, []);

  const handlePlazoChange = useCallback((numberOfDays) => {
    setPlazo(numberOfDays);
  }, []);

  const handleFrecuenciaChange = useCallback((selectedFrecuencia) => {
    setFrecuencia(selectedFrecuencia);
  }, []);

  const handleHorasChange = useCallback((totalHours, selectedIntervals, selectedOptions) => {
    setHorasSeleccionadas({ horas: totalHours, intervalos: selectedIntervals, options: selectedOptions });
  }, []);

  const handleDuracionChange = useCallback((selectedDuracion) => {
    setDuracion(selectedDuracion);
  }, []);

  const handleIncludeInstallingBuildingsChange = useCallback((isChecked) => {
    setIncludeInstallingBuildingsUserSelection(isChecked);
    setResetInventario(true);
  }, []);

  const memoizedInventarioData = useMemo(() => inventarioData, [inventarioData]);
  const memoizedHorasSeleccionadas = useMemo(() => horasSeleccionadas, [horasSeleccionadas]);

  if (loading) {
    return <div>Cargando...</div>;
  }

  return (
    <div className="cotizador-programmatic-wrapper">
      <div className="calculator-fixed">
        <CotizadorProgrammaticCalculadora
          inventarioData={memoizedInventarioData}
          plazo={plazo}
          frecuencia={frecuencia}
          horasSeleccionadas={memoizedHorasSeleccionadas}
          duracion={duracion}
          isProgrammaticSelected={isProgrammaticSelected}
          onIncludeInstallingBuildingsChange={handleIncludeInstallingBuildingsChange}
          cotizacionCode={oportunidad?.code}
          includeInstallingBuildings={includeInstallingBuildingsUserSelection}
          isEditMode={true}
          cliente={oportunidad?.cliente || ""}
          opportunityId={id}
          opportunityData={oportunidad}
        />
      </div>
      <div className="controls-container">
        <div className="controls-row">
          <CotizadorProgrammaticHorasOn
            onHorasChange={handleHorasChange}
            onProgrammaticChange={handleProgrammaticChange}
            onDigitalChange={handleDigitalChange}
            defaultHoras={memoizedHorasSeleccionadas.horas}
            defaultIntervalos={memoizedHorasSeleccionadas.intervalos}
            defaultOptions={memoizedHorasSeleccionadas.options}
            isProgrammaticSelected={isProgrammaticSelected}
            isDigitalSelected={isDigitalSelected}
            isEditMode={true}
          />
          <CotizadorProgrammaticInventario
            onSubmit={handleSubmitInventario}
            isProgrammaticSelected={isProgrammaticSelected}
            resetInventario={resetInventario}
            onResetComplete={() => setResetInventario(false)}
            includeInstallingBuildings={includeInstallingBuildingsUserSelection}
            defaultEdificios={initialEdificios}
            defaultEdificiosAscensores={defaultEdificiosAscensores}
            defaultEdificiosTotems={defaultEdificiosTotems}
            defaultEdificiosEspera={defaultEdificiosEspera}
            defaultEdificiosCowork={defaultEdificiosCowork}
          />
          <CotizadorProgrammaticPlazo
            onPlazoChange={handlePlazoChange}
            defaultPlazo={plazo}
            resetCounter={resetCounter}
          />
          <CotizadorProgrammaticFrecuencia
            onFrecuenciaChange={handleFrecuenciaChange}
            isProgrammaticSelected={isProgrammaticSelected}
            isDigitalSelected={isDigitalSelected}
            selectedOption={memoizedHorasSeleccionadas.options[0]?.label || ""}
            plazo={plazo}
            defaultFrecuencia={frecuencia}
            resetCounter={resetCounter}
          />
          <CotizadorProgrammaticDuracion
            onDuracionChange={handleDuracionChange}
            defaultDuracion={duracion}
            resetCounter={resetCounter}
          />
        </div>
      </div>
    </div>
  );
};
